import React, { useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { StyleType } from './style-type-form';
import { Localize, useTranslate } from '@agilepoint/wat-react';
import { IRadioGroupItemProps, StackPanel, Typography } from '@agilepoint/wat-react-ux';
import { ReactComponent as GeneralSetting } from '../../../assets/svg/general-settings.svg';
import { ReactComponent as Home } from '../../../assets/svg/home.svg';
import { ReactComponent as InactiveDelegation } from '../../../assets/svg/inactive-delegation.svg';
import { ReactComponent as LoginField } from '../../../assets/svg/login-field.svg';
import { ReactComponent as MyApps } from '../../../assets/svg/my-apps-new.svg';
import { ReactComponent as MyTasks } from '../../../assets/svg/my-tasks.svg';
import { ReactComponent as ReminderPlanForTheDay } from '../../../assets/svg/reminder-plan-for-the-day.svg';
import { AppStyleFields, PreviewWrapper, StyledNavigationButtons, StyledStylingContainer } from '../styled-components';
import { StyledStackPanel } from 'common';

interface IStylingFormProps {
    containerHeight?: string;
    headerLabel?: string;
    subHeading?: string;
}

export const StylingForm = React.memo((props: IStylingFormProps) => {
    const { headerLabel, subHeading } = props;
    const translate = useTranslate();
    const options = [{
        name: translate('modules.createNewApp.steps.styling.predefinedStylingForm'),
        value: 'predefined',
        key: 'predefinedColor'
    }, {
        name: translate('modules.createNewApp.steps.styling.customStylingForm'),
        value: 'custom',
        key: 'customColor'
    }];

    const onRenderItem = React.useCallback((itemProps: IRadioGroupItemProps) => <StyleType {...itemProps} />, []);

    return (
        <StyledStackPanel margin='30px 0px 0px 0px' alignment="center" width="100%" height={props.containerHeight ?? 'calc(100% - 111px)'} orientation='vertical' gap="40">
            <StackPanel gap="10" orientation='vertical' alignment="center" width='100%'>
                <Typography.H2 title={headerLabel ? headerLabel : translate('modules.createNewApp.steps.styling.stylingHeader')}>
                    {headerLabel ? headerLabel : <Localize translate='modules.createNewApp.steps.styling.stylingHeader' />}
                </Typography.H2>
                {subHeading ? <Typography.Medium title={subHeading}>{subHeading}</Typography.Medium> : <></>}
            </StackPanel>
            <StyledStylingContainer flexDirection='row' overflowY='auto' height='calc(100% - 127px)' width="100%" alignItems="center">
                <AppStyleFields groupName='styles' name='appStyles' gap="30" options={options} orientation="vertical" onRenderItem={onRenderItem} />
                <MobileAppPreview />
            </StyledStylingContainer>
        </StyledStackPanel>
    );
});

const MobileAppPreview = () => {
    const { watch } = useFormContext();
    const isPredefinedColorSelected = watch('appStyles') === 'predefinedColor';
    const menuColor = isPredefinedColorSelected ? watch('predefinedColor.color') : watch('customColor.menuColor');
    const translate = useTranslate();
    const headerWrapperRef = useRef<HTMLDivElement>(null);

    const onLeftClick = React.useCallback(() => {
        headerWrapperRef.current?.scrollBy({ left: -150, behavior: 'smooth' });
    }, []);

    const onRightClick = React.useCallback(() => {
        headerWrapperRef.current?.scrollBy({ left: 150, behavior: 'smooth' });
    }, []);

    return (
        <>
            <StyledNavigationButtons iconName='ap-left-arrow-thin2.outline' onClick={onLeftClick} />
            <PreviewWrapper orientation='horizontal' elementRef={headerWrapperRef} gap="40" height='100%' width='calc(100% - 395px);'>
                <StackPanel orientation='vertical' gap="10" alignment="center" height='462px' width='224px'>
                    <Typography.H6 title={translate('modules.createNewApp.steps.styling.stylingViewScreens.signInScreen')}>
                        <Localize translate={'modules.createNewApp.steps.styling.stylingViewScreens.signInScreen'} /></Typography.H6>
                    <LoginField style={{ fill: menuColor }} />
                </StackPanel>
                <StackPanel orientation='vertical' gap="10" alignment="center" height='462px' width='224px'>
                    <Typography.H6 title={translate('modules.createNewApp.steps.styling.stylingViewScreens.planner')}>
                        <Localize translate={'modules.createNewApp.steps.styling.stylingViewScreens.planner'} /></Typography.H6>
                    <ReminderPlanForTheDay style={{ fill: menuColor }} />
                </StackPanel>
                <StackPanel orientation='vertical' gap="10" alignment="center" height='462px' width='224px'>
                    <Typography.H6 title={translate('modules.createNewApp.steps.styling.stylingViewScreens.home')}>
                        <Localize translate={'modules.createNewApp.steps.styling.stylingViewScreens.home'} /></Typography.H6>
                    <Home style={{ fill: menuColor }} />
                </StackPanel>
                <StackPanel orientation='vertical' gap="10" alignment="center" height='462px' width='224px'>
                    <Typography.H6 title={translate('modules.createNewApp.steps.styling.stylingViewScreens.myApps')}>
                        <Localize translate={'modules.createNewApp.steps.styling.stylingViewScreens.myApps'} /></Typography.H6>
                    <MyApps style={{ fill: menuColor }} />
                </StackPanel>
                <StackPanel orientation='vertical' gap="10" alignment="center" height='462px' width='224px'>
                    <Typography.H6 title={translate('modules.createNewApp.steps.styling.stylingViewScreens.myTasks')}>
                        <Localize translate={'modules.createNewApp.steps.styling.stylingViewScreens.myTasks'} /></Typography.H6>
                    <MyTasks style={{ fill: menuColor }} />
                </StackPanel>
                <StackPanel orientation='vertical' gap="10" alignment="center" height='462px' width='224px'>
                    <Typography.H6 title={translate('modules.createNewApp.steps.styling.stylingViewScreens.delegations')}>
                        <Localize translate={'modules.createNewApp.steps.styling.stylingViewScreens.delegations'} /></Typography.H6>
                    <InactiveDelegation style={{ fill: menuColor }} />
                </StackPanel>
                <StackPanel orientation='vertical' gap="10" alignment="center" height='462px' width='224px'>
                    <Typography.H6 title={translate('modules.createNewApp.steps.styling.stylingViewScreens.settings')}>
                        <Localize translate={'modules.createNewApp.steps.styling.stylingViewScreens.settings'} /></Typography.H6>
                    <GeneralSetting style={{ fill: menuColor }} />
                </StackPanel>
            </PreviewWrapper>
            <StyledNavigationButtons iconName='ap-right-arrow-thin2.outline' onClick={onRightClick} />
        </>
    );
};
